import { Box, Chip, Typography } from '@mui/material';
import { FC, Fragment } from 'react';

const FACTOR_COLOR = '#8a5a00';
const FACTOR_SOFT = '#fff4dc';
const FACTOR_LINE = '#f0d9a6';

const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/;

type FactorName = '重量係数' | '体積係数';

export type FieldFactor = { name: FactorName; factor: number };

// Context の係数の型は number | null だが、ルールを選んで入れた係数は API が decimal 列を
// JSON の文字列（"1.2"）で返したものがそのまま入っている。Process が送る値の形を変えないよう、
// Context では直さず表示の側で数値に読む。
export type FactorInput = number | string | null | undefined;

const toFactor = (factor: FactorInput): number | null => {
  if (typeof factor === 'number') return Number.isFinite(factor) ? factor : null;
  if (typeof factor === 'string' && PLAIN_NUMBER.test(factor.trim())) return Number(factor);
  return null;
};

export const factorOfField = (
  key: string,
  weightFactor: FactorInput,
  volumeFactor: FactorInput,
): FieldFactor | null => {
  const weight = toFactor(weightFactor);
  const volume = toFactor(volumeFactor);
  if (key === 'item_total_weight_kg' && weight !== null) return { name: '重量係数', factor: weight };
  if (key === 'item_total_volume_m3' && volume !== null) return { name: '体積係数', factor: volume };
  return null;
};

const decimals = (text: string) => (text.split('.')[1] ?? '').length;

// 取込（API の BigDecimal）と同じく小数の誤差を出さないよう、桁をそろえて整数で掛ける。
// 数字だけの文字列でなければ API と同じ掛け算にならないので null を返す。
export const multiplyByFactor = (value: string, factor: number): string | null => {
  const left = value.trim();
  const right = String(factor);
  if (!PLAIN_NUMBER.test(left) || !PLAIN_NUMBER.test(right)) return null;

  const product = Math.round(Number(left) * 10 ** decimals(left)) * Math.round(factor * 10 ** decimals(right));
  return String(product / 10 ** (decimals(left) + decimals(right)));
};

type FactorNoticeProps = {
  weightFactor: FactorInput;
  volumeFactor: FactorInput;
};

const FactorNotice: FC<FactorNoticeProps> = ({ weightFactor, volumeFactor }) => {
  const parts = [
    factorOfField('item_total_weight_kg', weightFactor, null),
    factorOfField('item_total_volume_m3', null, volumeFactor),
  ].filter((part) => part !== null);
  if (parts.length === 0) return null;

  return (
    <Box
      role="note"
      sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
        backgroundColor: FACTOR_SOFT,
        border: `1px solid ${FACTOR_LINE}`,
        borderRadius: 2,
        paddingX: 2,
        paddingY: 1.5,
      }}
    >
      <Box
        aria-hidden
        sx={{
          flex: 'none',
          width: 22,
          height: 22,
          borderRadius: '50%',
          backgroundColor: FACTOR_COLOR,
          color: FACTOR_SOFT,
          display: 'grid',
          placeItems: 'center',
          fontWeight: 700,
          fontSize: 13,
        }}
      >
        ×
      </Box>
      <Typography variant="body2">
        1 ページ目の詳細設定で{' '}
        {parts.map((part, index) => (
          <Fragment key={part.name}>
            {index > 0 && '・'}
            {part.name} <b>×{part.factor}</b>
          </Fragment>
        ))}
        {' '}が入っています。元データ列から取得した値に掛けて取り込みます。固定値には掛けません。
      </Typography>
    </Box>
  );
};

export const FactorChip: FC<FieldFactor & { fixed: boolean }> = ({ name, factor, fixed }) => (
  <Chip
    size="small"
    variant="outlined"
    title={name}
    label={`係数 ×${factor}`}
    sx={fixed
      ? { color: 'text.secondary', textDecoration: 'line-through' }
      : { color: FACTOR_COLOR, backgroundColor: FACTOR_SOFT, borderColor: FACTOR_LINE, fontWeight: 700 }}
  />
);

export const FactorHint: FC<FieldFactor & { fixed: boolean }> = ({ name, factor, fixed }) => (
  <Typography variant="caption" sx={{ color: FACTOR_COLOR }}>
    {fixed ? `固定値には${name}（×${factor}）を掛けません` : `取り込むときに${name}（×${factor}）を掛けます`}
  </Typography>
);

type FactorPreviewProps = { value: string; factor: number; fixed: boolean };

export const FactorPreview: FC<FactorPreviewProps> = ({ value, factor, fixed }) => {
  if (fixed) {
    return (
      <>
        <span>{value}</span>
        <Typography component="span" variant="caption" color="text.secondary" display="block">固定値（係数なし）</Typography>
      </>
    );
  }

  const multiplied = multiplyByFactor(value, factor);
  if (multiplied === null) return <span>{value}</span>;

  return (
    <>
      <span>{multiplied}</span>
      <Typography component="span" variant="caption" display="block" sx={{ color: FACTOR_COLOR }}>
        {`${value} × ${factor}`}
      </Typography>
    </>
  );
};

export default FactorNotice;
